import { Injectable } from '@angular/core';
import { ConsoleLogEntry, ConsoleLogLevel } from '../types/console-log-entry';
import { RedactionService } from '../../security/redaction.service';
import { RingBuffer } from '../storage/ring-buffer';

@Injectable({
  providedIn: 'root',
})
export class ConsoleCaptureCollector {
  private readonly entries = new RingBuffer<ConsoleLogEntry>(100);
  private started = false;
  private readonly originalConsole: Partial<Record<ConsoleLogLevel, (...args: unknown[]) => void>> = {};

  constructor(private readonly redactionService: RedactionService) {}

  start(): void {
    if (this.started) {
      return;
    }

    this.started = true;

    for (const level of [
      'log',
      'info',
      'warn',
      'error',
      'debug',
      'trace',
      'assert',
      'group',
      'groupCollapsed',
      'groupEnd',
      'table',
      'time',
      'timeEnd',
      'timeLog',
      'timeStamp',
      'dir',
      'dirxml',
      'profile',
      'profileEnd',
    ] as const) {
      this.patchMethod(level);
    }
  }

  stop(): void {
    if (!this.started) {
      return;
    }

    this.started = false;

    for (const level of [
      'log',
      'info',
      'warn',
      'error',
      'debug',
      'trace',
      'assert',
      'group',
      'groupCollapsed',
      'groupEnd',
      'table',
      'time',
      'timeEnd',
      'timeLog',
      'timeStamp',
      'dir',
      'dirxml',
      'profile',
      'profileEnd',
    ] as const) {
      const original = this.originalConsole[level];
      if (original) {
        console[level] = original;
      }
    }
  }

  clear(): void {
    this.entries.clear();
  }

  getEntries(): readonly ConsoleLogEntry[] {
    return this.entries.toArray();
  }

  private patchMethod(level: ConsoleLogLevel): void {
    const consoleObject = console as unknown as Record<string, (...args: unknown[]) => void>;
    const original = consoleObject[level].bind(console);
    this.originalConsole[level] = original;

    consoleObject[level] = (...args: unknown[]): void => {
      // Always pass through to the real console first so DevTools source attribution
      // points to the actual caller, not this interceptor.
      original(...args);

      // Capture silently — any serialization or ring-buffer error must never
      // propagate to the application.
      try {
        this.entries.push({
          level,
          message: this.serializeArgs(args),
          args: [],
          timestamp: new Date().toISOString(),
        });
      } catch {
        // intentionally swallowed — diagnostic capture must be non-disruptive
      }
    };
  }

  private serializeArgs(args: unknown[]): string {
    if (args.length === 0) {
      return '';
    }

    return args
      .map((arg) => {
        try {
          if (arg instanceof Error) {
            const details = [arg.name, arg.message].filter(Boolean).join(': ');
            const stack = arg.stack ? this.redactionService.redactText(arg.stack) : '';
            return stack ? `${details}\n${stack}` : details;
          }

          return this.redactionService.redactText(arg);
        } catch {
          return '[unserializable]';
        }
      })
      .join(' ');
  }
}
